import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download, CheckCircle, RefreshCw, Upload, FileText } from 'lucide-react';
import { fetchAllInvoices, fetchInvoicesForMonth, markInvoicePaid, updateInvoiceStatus, generateInvoices, uploadMt940, matchMt940, getSepaSummary, getSepaPayouts, downloadSepaExport, downloadAdminInvoicePdf } from '../services/invoiceApi';
import type { InvoiceStatus } from '../services/invoiceApi';
import { Button, Card } from '../components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

type TabMode = 'facturen' | 'mt940' | 'sepa';

export const AdminInvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<'all' | 'month'>('all');
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [tab, setTab] = useState<TabMode>('facturen');

  // MT940 state
  const [mt940Transactions, setMt940Transactions] = useState<any[]>([]);
  const [mt940Matches, setMt940Matches] = useState<any[]>([]);
  const [mt940Unmatched, setMt940Unmatched] = useState<any[]>([]);
  const [markPaidModal, setMarkPaidModal] = useState<{ match: any } | null>(null);
  const [markPaidConfirm, setMarkPaidConfirm] = useState(false);
  const mt940FileInputRef = useRef<HTMLInputElement>(null);

  // SEPA tab state
  const [sepaYear, setSepaYear] = useState<number>(new Date().getFullYear());
  const [sepaMonth, setSepaMonth] = useState<number>(new Date().getMonth() + 1);
  const [sepaSummary, setSepaSummary] = useState<{
    totalPaidByEmployer: number;
    totalPayoutToProfessional: number;
    belagomFee: number;
    count: number;
  } | null>(null);
  const [sepaPayouts, setSepaPayouts] = useState<Array<{ payout_id: string; invoice_number: string; professional_name: string; payout_amount: number }>>([]);
  const [sepaSelectedPayoutIds, setSepaSelectedPayoutIds] = useState<Set<string>>(new Set());
  const [sepaLoading, setSepaLoading] = useState(false);
  const [sepaDownloading, setSepaDownloading] = useState(false);

  const getErrorMessage = (error: unknown, fallback: string) => {
    if (!error || typeof error !== 'object') return fallback;
    const err = error as any;
    const response = err.response;
    const responseMessage =
      response && typeof response === 'object' ? response?.data?.message : undefined;
    return responseMessage || err.message || fallback;
  };

  // Fetch all invoices
  const { data: allInvoicesData, isLoading: allLoading, refetch: refetchAll } = useQuery({
    queryKey: ['admin-all-invoices'],
    queryFn: async () => {
      const res = await fetchAllInvoices();
      return Array.isArray(res?.data) ? res?.data : (res?.data?.data || []);
    },
    enabled: viewMode === 'all',
  });

  // Fetch invoices for month (only when year/month are valid to avoid NaN in API)
  const yearMonthValid = Number.isInteger(year) && Number.isInteger(month) && month >= 1 && month <= 12;
  const { data: monthInvoicesData, isLoading: monthLoading, refetch: refetchMonth } = useQuery({
    queryKey: ['admin-invoices-month', year, month],
    queryFn: async () => {
      const res = await fetchInvoicesForMonth(year, month);
      return Array.isArray(res?.data) ? res?.data : (res?.data?.data || []);
    },
    enabled: viewMode === 'month' && yearMonthValid,
  });

  const invoices = viewMode === 'all' ? (allInvoicesData || []) : (monthInvoicesData || []);
  const isLoading = viewMode === 'all' ? allLoading : monthLoading;

  // Mark invoice as paid mutation (paymentReference optional, for MT940)
  const markPaidMutation = useMutation({
    mutationFn: async ({ invoiceId, paymentReference }: { invoiceId: string; paymentReference?: string }) => {
      await markInvoicePaid(invoiceId, paymentReference);
    },
    onSuccess: (_data, { invoiceId, paymentReference }) => {
      toast.success(t('admin.invoices.markedAsPaid'));
      if (viewMode === 'all') {
        queryClient.invalidateQueries({ queryKey: ['admin-all-invoices'] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['admin-invoices-month', year, month] });
      }
      if (paymentReference != null) {
        setMarkPaidModal(null);
        setMarkPaidConfirm(false);
        setMt940Matches((prev) => prev.filter((m) => m.invoice.id !== invoiceId));
      }
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, t('admin.invoices.errorMarkPaid')));
    }
  });

  // Handmatig status aanpassen (bijv. betaald in delen)
  const updateStatusMutation = useMutation({
    mutationFn: async ({ invoiceId, status, paymentReference }: { invoiceId: string; status: InvoiceStatus; paymentReference?: string }) => {
      await updateInvoiceStatus(invoiceId, status, paymentReference);
    },
    onSuccess: (_data, { status }) => {
      const statusLabel = status === 'paid' ? t('admin.invoices.statusPaid') : status;
      toast.success(t('admin.invoices.statusUpdated', { status: statusLabel }));
      if (viewMode === 'all') {
        queryClient.invalidateQueries({ queryKey: ['admin-all-invoices'] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['admin-invoices-month', year, month] });
      }
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, t('admin.invoices.errorUpdateStatus')));
    }
  });

  // Generate invoices mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      await generateInvoices(year, month);
    },
    onSuccess: () => {
      toast.success(t('admin.invoices.invoicesGenerated'));
      if (viewMode === 'month') {
        refetchMonth();
      }
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, t('admin.invoices.errorGenerate')));
    }
  });

  const handleMarkPaid = (invoiceId: string, paymentReference?: string) => {
    if (!confirm(t('admin.invoices.confirmMarkPaidQuick'))) return;
    markPaidMutation.mutate({ invoiceId, paymentReference });
  };

  const handleMt940Upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadMt940(file);
      const txs = res?.data?.transactions ?? [];
      setMt940Transactions(txs);
      setMt940Matches([]);
      setMt940Unmatched([]);
      if (txs.length === 0) {
        toast.error(res?.data?.message || t('admin.invoices.noMt940Transactions'));
      } else {
        toast.success(`${txs.length} ${t('admin.invoices.transactionsLoaded')}`);
      }
    } catch (err: any) {
      toast.error(getErrorMessage(err, t('admin.invoices.errorUploadMt940')));
    }
    e.target.value = '';
  };

  const handleMt940Match = async () => {
    if (mt940Transactions.length === 0) {
      toast.error(t('admin.invoices.uploadMt940First'));
      return;
    }
    try {
      const res = await matchMt940(mt940Transactions);
      setMt940Matches(res?.data?.matches ?? []);
      setMt940Unmatched(res?.data?.unmatchedTransactions ?? []);
      toast.success(t('admin.invoices.matchResult', { matched: res?.data?.matches?.length ?? 0, unmatched: res?.data?.unmatchedTransactions?.length ?? 0 }));
    } catch (err: any) {
      toast.error(getErrorMessage(err, t('admin.invoices.errorMatch')));
    }
  };

  const openMarkPaidModal = (match: any) => setMarkPaidModal({ match });
  const confirmMarkPaidFromMt940 = () => {
    if (!markPaidModal) return;
    const { match } = markPaidModal;
    const ref = match.transaction?.reference || match.transaction?.id || '';
    markPaidMutation.mutate({ invoiceId: match.invoice.id, paymentReference: ref });
  };

  const loadSepaSummary = async () => {
    setSepaLoading(true);
    setSepaSummary(null);
    setSepaPayouts([]);
    setSepaSelectedPayoutIds(new Set());
    try {
      const [summaryRes, payoutsRes] = await Promise.all([
        getSepaSummary(sepaYear, sepaMonth),
        getSepaPayouts(sepaYear, sepaMonth),
      ]);
      const d = summaryRes?.data ?? {};
      setSepaSummary({
        totalPaidByEmployer: d?.totalPaidByEmployer ?? 0,
        totalPayoutToProfessional: d?.totalPayoutToProfessional ?? 0,
        belagomFee: d?.belagomFee ?? 0,
        count: d?.count ?? 0,
      });
      const payouts = payoutsRes?.data?.payouts ?? [];
      setSepaPayouts(payouts);
      setSepaSelectedPayoutIds(new Set(payouts.map((p: any) => p.payout_id)));
      if ((d?.count ?? 0) === 0) {
        toast(t('admin.invoices.noPendingPayouts'));
      }
    } catch (err: any) {
      toast.error(getErrorMessage(err, t('admin.invoices.errorLoadSummary')));
    } finally {
      setSepaLoading(false);
    }
  };

  const toggleSepaPayout = (payoutId: string) => {
    setSepaSelectedPayoutIds((prev) => {
      const next = new Set(prev);
      if (next.has(payoutId)) next.delete(payoutId);
      else next.add(payoutId);
      return next;
    });
  };

  const selectAllSepaPayouts = (checked: boolean) => {
    setSepaSelectedPayoutIds(checked ? new Set(sepaPayouts.map((p) => p.payout_id)) : new Set());
  };

  const handleDownloadSepa = async () => {
    const ids = Array.from(sepaSelectedPayoutIds);
    if (ids.length === 0) {
      toast.error(t('admin.invoices.selectPayoutFirst'));
      return;
    }
    setSepaDownloading(true);
    try {
      const blob = await downloadSepaExport(sepaYear, sepaMonth, ids);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sepa-export-${sepaYear}-${sepaMonth}-${Date.now()}.xml`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('admin.invoices.sepaDownloaded'));
    } catch (err: any) {
      // Backend returns JSON errors; with responseType: 'blob' the body is a Blob, so parse it for the message
      let message = t('admin.invoices.errorSepaDownload');
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const json = JSON.parse(text);
          if (json?.message) message = json.message;
        } catch (_) { /* ignore */ }
      } else if (data?.message) {
        message = data.message;
      }
      toast.error(message);
    } finally {
      setSepaDownloading(false);
    }
  };

  const handleGenerate = () => {
    if (!confirm(t('admin.invoices.confirmGenerate', { year, month }))) return;
    generateMutation.mutate();
  };

  const calculateBelagomFee = (invoice: any) => {
    return parseFloat(invoice.belagom_fee_amount || invoice.platform_fee_amount || 0);
  };

  const calculateProfessionalPayout = (invoice: any) => {
    if (invoice.professional_payout != null) {
      return parseFloat(invoice.professional_payout);
    }
    const grossAmount = parseFloat(invoice.gross_amount || 0);
    const belagomFee = calculateBelagomFee(invoice);
    return Math.max(0, grossAmount - belagomFee);
  };

  const getGrossAmount = (invoice: any) => {
    const hasFeeOrPayout =
      invoice.professional_payout != null ||
      invoice.belagom_fee_amount != null ||
      invoice.platform_fee_amount != null;

    if (hasFeeOrPayout) {
      return calculateBelagomFee(invoice) + calculateProfessionalPayout(invoice);
    }

    const grossAmount = parseFloat(invoice.gross_amount || 0);
    return Number.isFinite(grossAmount) ? grossAmount : 0;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => navigate('/admin')}
          className="flex items-center"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('admin.invoices.backToAdmin')}
        </Button>
      </div>

      <div className="mb-6">
        {/* Tabs: Facturen | MT940 upload | SEPA uitbetaling */}
        <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-200">
          <button
            onClick={() => setTab('facturen')}
            className={`px-4 py-2 rounded-t-lg font-medium ${
              tab === 'facturen' ? 'bg-white border border-b-0 border-gray-200 -mb-px' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t('admin.invoices.tabInvoices')}
          </button>
          <button
            onClick={() => setTab('mt940')}
            className={`px-4 py-2 rounded-t-lg font-medium ${
              tab === 'mt940' ? 'bg-white border border-b-0 border-gray-200 -mb-px' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t('admin.invoices.tabMt940')}
          </button>
          <button
            onClick={() => setTab('sepa')}
            className={`px-4 py-2 rounded-t-lg font-medium ${
              tab === 'sepa' ? 'bg-white border border-b-0 border-gray-200 -mb-px' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t('admin.invoices.tabSepa')}
          </button>
        </div>

        {/* View Mode Toggle - only in Facturen tab */}
        {tab === 'facturen' && (
        <div className="flex items-center gap-4 mb-4">
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('all')}
              className={`px-4 py-2 rounded-lg font-medium ${
                viewMode === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {t('admin.invoices.allInvoices')}
            </button>
            <button
              onClick={() => setViewMode('month')}
              className={`px-4 py-2 rounded-lg font-medium ${
                viewMode === 'month'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {t('admin.invoices.perMonth')}
            </button>
          </div>

          {viewMode === 'month' && (
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={year}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (!Number.isNaN(n)) setYear(Math.max(2000, Math.min(2100, n)));
                }}
                min={2000}
                max={2100}
                className="border border-gray-300 rounded-lg px-3 py-2 w-24"
                placeholder={t('admin.invoices.yearPlaceholder')}
              />
              <input
                type="number"
                value={month}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (!Number.isNaN(n)) setMonth(Math.max(1, Math.min(12, n)));
                }}
                min={1}
                max={12}
                className="border border-gray-300 rounded-lg px-3 py-2 w-20"
                placeholder={t('admin.invoices.monthPlaceholder')}
              />
              <Button
                onClick={handleGenerate}
                disabled={generateMutation.isPending}
                variant="outline"
              >
                {generateMutation.isPending ? t('admin.invoices.generating') : t('admin.invoices.generateInvoices')}
              </Button>
              <Button
                onClick={() => refetchMonth()}
                disabled={monthLoading}
                variant="outline"
              >
                <RefreshCw className={`w-4 h-4 ${monthLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          )}

          {viewMode === 'all' && (
            <Button
              onClick={() => refetchAll()}
              disabled={allLoading}
              variant="outline"
            >
              <RefreshCw className={`w-4 h-4 ${allLoading ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
        )}

        {tab === 'mt940' && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-3">
              {t('admin.invoices.mt940Description')}
            </p>
            <div className="flex flex-wrap items-center gap-4">
            <input
              ref={mt940FileInputRef}
              type="file"
              accept=".sta,.txt,.mt940,.940,text/plain,application/octet-stream"
              className="hidden"
              onChange={handleMt940Upload}
            />
            <Button variant="outline" onClick={() => mt940FileInputRef.current?.click()}>
              <Upload className="w-4 h-4 mr-2" />
              {t('admin.invoices.mt940Upload')}
            </Button>
            <Button
              variant="outline"
              disabled={mt940Transactions.length === 0}
              onClick={handleMt940Match}
            >
              <FileText className="w-4 h-4 mr-2" />
              {t('admin.invoices.mt940Match')}
            </Button>
            {mt940Transactions.length > 0 && (
              <span className="text-sm text-gray-500">
                {mt940Transactions.length} {t('admin.invoices.transactionsLoaded')}
              </span>
            )}
            </div>
          </div>
        )}

        {tab === 'sepa' && (
          <div className="mb-4 flex flex-wrap items-center gap-4">
            <input
              type="number"
              value={sepaYear}
              onChange={(e) => setSepaYear(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 w-24"
              placeholder={t('admin.invoices.yearPlaceholder')}
            />
            <input
              type="number"
              value={sepaMonth}
              onChange={(e) => setSepaMonth(Number(e.target.value))}
              min={1}
              max={12}
              className="border border-gray-300 rounded-lg px-3 py-2 w-20"
              placeholder={t('admin.invoices.monthPlaceholder')}
            />
            <Button variant="outline" onClick={loadSepaSummary} disabled={sepaLoading}>
              {sepaLoading ? t('admin.invoices.loading') : t('admin.invoices.loadOverview')}
            </Button>
            <Button
              variant="primary"
              onClick={handleDownloadSepa}
              disabled={sepaDownloading || (sepaSummary?.count ?? 0) === 0 || sepaSelectedPayoutIds.size === 0}
            >
              {sepaDownloading
                ? t('admin.invoices.downloading')
                : sepaSummary
                ? t('admin.invoices.downloadSepaSelected', { count: sepaSelectedPayoutIds.size })
                : t('admin.invoices.downloadSepa')}
            </Button>
          </div>
        )}
      </div>

      {/* SEPA overzicht - only when summary loaded */}
      {tab === 'sepa' && sepaSummary != null && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <div className="p-4">
              <div className="text-sm text-gray-600">{t('admin.invoices.sepaCount')}</div>
              <div className="text-2xl font-bold">{sepaSummary.count}</div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="text-sm text-gray-600">{t('admin.invoices.sepaTotalPaid')}</div>
              <div className="text-2xl font-bold">€{sepaSummary.totalPaidByEmployer.toFixed(2)}</div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="text-sm text-gray-600">{t('admin.invoices.sepaBelagomFee')}</div>
              <div className="text-2xl font-bold text-blue-600">€{sepaSummary.belagomFee.toFixed(2)}</div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="text-sm text-gray-600">{t('admin.invoices.sepaToProfessional')}</div>
              <div className="text-2xl font-bold text-green-600">€{sepaSummary.totalPayoutToProfessional.toFixed(2)}</div>
              {sepaSelectedPayoutIds.size > 0 && sepaSelectedPayoutIds.size < sepaSummary.count && (
                <div className="text-xs text-gray-500 mt-1">
                  {t('admin.invoices.sepaSelected', {
                    selected: sepaSelectedPayoutIds.size,
                    total: sepaSummary.count,
                    amount: sepaPayouts
                      .filter((p) => sepaSelectedPayoutIds.has(p.payout_id))
                      .reduce((s, p) => s + p.payout_amount, 0)
                      .toFixed(2)
                  })}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
      {tab === 'sepa' && sepaSummary != null && (
        <p className="text-xs text-gray-500 mb-6">
          {t('admin.invoices.sepaNote')}
        </p>
      )}

      {/* SEPA uitbetalingen lijst met vinkjes */}
      {tab === 'sepa' && sepaPayouts.length > 0 && (
        <Card className="mb-6">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">{t('admin.invoices.sepaPayouts')}</h3>
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sepaSelectedPayoutIds.size === sepaPayouts.length}
                  onChange={(e) => selectAllSepaPayouts(e.target.checked)}
                  className="rounded border-gray-300"
                />
                {t('admin.invoices.selectAll')}
              </label>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              {t('admin.invoices.sepaPayoutHint')}
            </p>
            <div className="overflow-x-auto max-h-80 overflow-y-auto border rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-3 w-10">
                      <input
                        type="checkbox"
                        checked={sepaSelectedPayoutIds.size === sepaPayouts.length}
                        onChange={(e) => selectAllSepaPayouts(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="text-left py-2 px-3">{t('admin.invoices.colInvoiceNr')}</th>
                    <th className="text-left py-2 px-3">{t('admin.invoices.colProfessional')}</th>
                    <th className="text-right py-2 px-3">{t('admin.invoices.colAmount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sepaPayouts.map((p) => (
                    <tr key={p.payout_id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3">
                        <input
                          type="checkbox"
                          checked={sepaSelectedPayoutIds.has(p.payout_id)}
                          onChange={() => toggleSepaPayout(p.payout_id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="py-2 px-3">{p.invoice_number}</td>
                      <td className="py-2 px-3">{p.professional_name}</td>
                      <td className="py-2 px-3 text-right">€{Number(p.payout_amount).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      )}

      {/* MT940 Matches + Unmatched */}
      {tab === 'mt940' && (mt940Matches.length > 0 || mt940Unmatched.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card>
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-3">{t('admin.invoices.mt940Matches')}</h3>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">{t('admin.invoices.mt940ColAmount')}</th>
                      <th className="text-left py-2">{t('admin.invoices.mt940ColInvoice')}</th>
                      <th className="text-left py-2">{t('admin.invoices.mt940ColConfidence')}</th>
                      <th className="text-left py-2">{t('admin.invoices.mt940ColAction')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mt940Matches.map((m, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-2">€{Number(m.transaction?.amount ?? 0).toFixed(2)}</td>
                        <td className="py-2">{m.invoice?.invoice_number}</td>
                        <td className="py-2">
                          {m.confidence === 'high'
                            ? t('admin.invoices.mt940ConfidenceHigh')
                            : m.confidence === 'medium'
                            ? t('admin.invoices.mt940ConfidenceMedium')
                            : t('admin.invoices.mt940ConfidenceLow')}
                        </td>
                        <td className="py-2">
                          {m.invoice?.status === 'paid' ? (
                            <span className="text-green-600">{t('admin.invoices.alreadyPaid')}</span>
                          ) : (
                            <button
                              type="button"
                              className="text-green-600 hover:text-green-800"
                              onClick={() => openMarkPaidModal(m)}
                            >
                              {t('admin.invoices.markAsPaid')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-3">{t('admin.invoices.mt940Unmatched')}</h3>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">{t('admin.invoices.mt940ColDate')}</th>
                      <th className="text-left py-2">{t('admin.invoices.mt940ColAmount')}</th>
                      <th className="text-left py-2">{t('admin.invoices.mt940ColReference')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mt940Unmatched.map((tx, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-2">{tx.date}</td>
                        <td className="py-2">€{Number(tx.amount ?? 0).toFixed(2)}</td>
                        <td className="py-2 truncate max-w-[120px]" title={tx.details || tx.reference}>{tx.details || tx.reference || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Modal: Bevestig markeer als betaald (MT940) */}
      {markPaidModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setMarkPaidModal(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('admin.invoices.confirmMarkPaid')}</h3>
            <p className="text-gray-600 mb-4">
              {t('admin.invoices.confirmMarkPaidText', {
                amount: Number(markPaidModal.match?.transaction?.amount ?? 0).toFixed(2),
                invoiceNumber: markPaidModal.match?.invoice?.invoice_number,
              })}
            </p>
            <label className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                checked={markPaidConfirm}
                onChange={(e) => setMarkPaidConfirm(e.target.checked)}
              />
              <span>{t('admin.invoices.confirmCheckbox')}</span>
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setMarkPaidModal(null); setMarkPaidConfirm(false); }}>
                {t('admin.invoices.cancel')}
              </Button>
              <Button
                disabled={!markPaidConfirm}
                onClick={confirmMarkPaidFromMt940}
              >
                {t('admin.invoices.confirmBtn')}
              </Button>
            </div>
          </div>
        </div>
        )}

      {/* Summary Cards - Facturen tab only */}
      {tab === 'facturen' && invoices.length > 0 && (() => {
        const subtotalExBtw = invoices.reduce((sum: number, inv: any) => sum + getGrossAmount(inv), 0);
        const vatRate = 1.21; // 21% BTW
        const totaalInclBtw = subtotalExBtw * vatRate;
        return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <Card>
            <div className="p-4">
              <div className="text-sm text-gray-600">{t('admin.invoices.totalInvoices')}</div>
              <div className="text-2xl font-bold">{invoices.length}</div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="text-sm text-gray-600">{t('admin.invoices.subtotal')}</div>
              <div className="text-2xl font-bold">
                €{subtotalExBtw.toFixed(2)}
              </div>
              <div className="text-xs text-gray-500 mt-1">{t('admin.invoices.subtotalNote')}</div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="text-sm text-gray-600">{t('admin.invoices.totalBelagomFee')}</div>
              <div className="text-2xl font-bold text-blue-600">
                €{invoices.reduce((sum: number, inv: any) => sum + calculateBelagomFee(inv), 0).toFixed(2)}
              </div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="text-sm text-gray-600">{t('admin.invoices.totalProfPayout')}</div>
              <div className="text-2xl font-bold text-green-600">
                €{invoices.reduce((sum: number, inv: any) => sum + calculateProfessionalPayout(inv), 0).toFixed(2)}
              </div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="text-sm text-gray-600">{t('admin.invoices.totalInclBtw')}</div>
              <div className="text-2xl font-bold text-gray-700">
                €{totaalInclBtw.toFixed(2)}
              </div>
              <div className="text-xs text-gray-500 mt-1">{t('admin.invoices.totalInclBtwNote')}</div>
            </div>
          </Card>
        </div>
        );
      })()}

      {/* Invoices Table - Facturen tab only */}
      {tab === 'facturen' && (
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.invoices.thInvoiceNr')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.invoices.thProfessional')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.invoices.thEmployer')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.invoices.thPeriod')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.invoices.thSubtotal')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.invoices.thBelagomFee')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.invoices.thProfPayout')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.invoices.thTotalInclBtw')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.invoices.thStatus')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.invoices.thActions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="px-6 py-8 text-center text-gray-500">
                    {t('admin.invoices.loadingInvoices')}
                  </td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-8 text-center text-gray-500">
                    {t('admin.invoices.noInvoices')}
                  </td>
                </tr>
              ) : (
                invoices.map((inv: any) => {
                  const belagomFee = calculateBelagomFee(inv);
                  const professionalPayout = calculateProfessionalPayout(inv);
                  const grossAmount = getGrossAmount(inv);
                  const vatPct = parseFloat(inv.vat_percentage ?? 21) / 100;
                  const totalInclBtw = grossAmount * (1 + vatPct);
                  const statusColors: { [key: string]: string } = {
                    'draft': 'bg-yellow-100 text-yellow-800',
                    'sent': 'bg-blue-100 text-blue-800',
                    'paid': 'bg-green-100 text-green-800',
                    'overdue': 'bg-red-100 text-red-800',
                    'cancelled': 'bg-gray-100 text-gray-800',
                  };

                  return (
                    <tr key={inv.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {inv.invoice_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {inv.professional_first_name && inv.professional_last_name
                          ? `${inv.professional_first_name} ${inv.professional_last_name}`
                          : inv.professional_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {inv.employer_first_name && inv.employer_last_name
                          ? `${inv.employer_first_name} ${inv.employer_last_name}`
                          : inv.employer_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {inv.period_start && inv.period_end
                          ? `${new Date(inv.period_start).toLocaleDateString('nl-NL')} - ${new Date(inv.period_end).toLocaleDateString('nl-NL')}`
                          : `${inv.invoice_year}-${String(inv.invoice_month).padStart(2, '0')}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                        €{grossAmount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-blue-600">
                        €{belagomFee.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-green-600">
                        €{professionalPayout.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600">
                        €{totalInclBtw.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={inv.status || 'draft'}
                          onChange={(e) => {
                            const newStatus = e.target.value as InvoiceStatus;
                            if (newStatus === inv.status) return;
                            updateStatusMutation.mutate({ invoiceId: inv.id, status: newStatus });
                          }}
                          disabled={updateStatusMutation.isPending}
                          className={`text-xs font-medium rounded-md border-0 py-1.5 pl-2 pr-6 focus:ring-2 focus:ring-blue-500 ${
                            statusColors[inv.status] || 'bg-gray-100 text-gray-800'
                          }`}
                          title={t('admin.invoices.statusChangeTooltip')}
                        >
                          <option value="draft">{t('admin.invoices.statusDraft')}</option>
                          <option value="sent">{t('admin.invoices.statusSent')}</option>
                          <option value="paid">{t('admin.invoices.statusPaid')}</option>
                          <option value="overdue">{t('admin.invoices.statusOverdue')}</option>
                          <option value="cancelled">{t('admin.invoices.statusCancelled')}</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await downloadAdminInvoicePdf(inv.id);
                              } catch (err) {
                                toast.error(getErrorMessage(err, t('admin.invoices.errorOpenPdf')));
                              }
                            }}
                            className="text-blue-600 hover:text-blue-800 flex items-center"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            PDF
                          </button>
                          {inv.status !== 'paid' && (
                            <button
                              onClick={() => handleMarkPaid(inv.id)}
                              disabled={markPaidMutation.isPending}
                              className="text-green-600 hover:text-green-800 flex items-center disabled:opacity-50"
                              type="button"
                              title={t('admin.invoices.quickMarkPaidTooltip')}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              {t('admin.invoices.paidBadge')}
                            </button>
                          )}
                          {inv.status === 'paid' && (
                            <span className="text-green-600 flex items-center">
                              <CheckCircle className="w-4 h-4 mr-1" />
                              {t('admin.invoices.paidBadge')}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
      )}
    </div>
  );
};

export default AdminInvoicesPage;
